import React, { useState, useEffect, useMemo } from "react";

function MakeDraftPick({ roomId, user, availableCards = [], currentUser, hotCards = [], socket }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [costFilter, setCostFilter] = useState(null);
  const [abilityFilter, setAbilityFilter] = useState(null);
  const [archetypes, setArchetypes] = useState([]); // 🔹 Store archetypes from backend
  const [archetypeFilter, setArchetypeFilter] = useState("All"); // 🔹 Selected archetype name

  // Fetch archetypes
  useEffect(() => {
    const fetchArchetypes = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}api/data/snapArchetypes`);
        if (!res.ok) throw new Error("Failed to fetch archetypes");
        const data = await res.json();
        setArchetypes(data);
      } catch (err) {
        console.error("Error fetching archetypes:", err);
      }
    };
    fetchArchetypes();
  }, []);

  // --- Filter logic ---
  const filteredCards = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return availableCards.filter((c) => {
      const name = c.cardname?.toLowerCase() ?? "";
      const desc = (c.carddesc || c.cardDesc || "").toLowerCase();
      const cost = Number(c.cardcost ?? c.cardCost ?? -1);

      // 🔹 Archetype match
      let matchesArchetype = true;
      if (archetypeFilter !== "All") {
        const selectedArch = archetypes.find(
          (a) => a.cardarchetype?.toLowerCase() === archetypeFilter.toLowerCase()
        );
        const archIds = selectedArch?.cardids || [];
        matchesArchetype = archIds.includes(c.id);
      }

      // 🔹 Search match
      const matchesName = name.includes(term);

      // 🔹 Cost match
      let matchesCost = true;
      if (costFilter === "0-1") matchesCost = cost === 0 || cost === 1;
      else if (costFilter === "6+") matchesCost = cost >= 6;
      else if (costFilter !== null) matchesCost = cost === Number(costFilter);

      // 🔹 Ability match
      let matchesAbility = true;
      if (abilityFilter) {
        const keyword = abilityFilter.toLowerCase();
        matchesAbility = desc.includes(keyword);
      }

      return matchesArchetype && matchesName && matchesCost && matchesAbility;
    });
  }, [availableCards, searchTerm, costFilter, abilityFilter, archetypeFilter, archetypes]);

  // --- Handle card click ---
  const [isPicking, setIsPicking] = useState(false); // prevent rapid double picks

  const handleCardClick = (cardId) => {
    if (!socket || !currentUser) return;

    // 1️⃣ Prevent picking if not this user's turn
    if (currentUser.id !== user.id) {
      alert("It's not your turn!");
      return;
    }

    // 2️⃣ Prevent double clicking (temporary lock)
    if (isPicking) return;

    // 3️⃣ Prevent picking already drafted card (client-side)
    const isAlreadyDrafted = !availableCards.some((c) => c.id === cardId);
    if (isAlreadyDrafted) {
      alert("This card has already been drafted!");
      return;
    }

    setIsPicking(true); // lock selection

    console.log(`🎯 ${user.username || user.userName} picking card ${cardId}`);
    socket.emit("makePick", { roomId, userId: user.id, cardId });

    // Re-enable picking once backend updates state (rough delay)
    setTimeout(() => setIsPicking(false), 1500);
  };

  // --- Filter button options ---
  const costOptions = ["0-1", 2, 3, 4, 5, "6+"];
  const abilityOptions = ["On Reveal", "Ongoing", "Activate", "End of Turn"];

  return (
    <div className="styled-info-modal" style={{ marginTop: "30px", textAlign: "center" }}>
      {/* Current turn indicator */}
      {currentUser && (
        <h2 className="header-title">
          Current Turn:{" "}
          <span style={{ color: "var(--snap-gold)" }}>
            {currentUser.username || currentUser.userName}
          </span>
        </h2>
      )}

      {/* 🔥 Hot cards */}
      {hotCards.length > 0 && (
        <div className="status-box hot-card" style={{ margin: "20px auto" }}>
          <h3 className="header-title" style={{ fontSize: "1.4rem" }}>
            🔥 Hot Cards
          </h3>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            {hotCards.map((c) => (
              <img
                key={c.id}
                src={c.cardhtml || c.cardHTML}
                alt={c.cardname}
                title={c.cardname}
                style={{
                  width: "110px",
                  borderRadius: "6px",
                  border: "2px solid var(--snap-gold)",
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* 🔍 Search bar */}
      <h3 className="header-title">Available Cards</h3>
      <input
        type="text"
        placeholder="Search cards by name..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="modern-input"
        style={{
          marginBottom: "15px",
          padding: "8px",
          width: "250px",
          borderRadius: "6px",
          border: "1px solid #ccc",
        }}
      />

      {/* 🪙 Cost filters */}
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          justifyContent: "center",
          gap: "8px",
          flexWrap: "wrap",
        }}
      >
        <button
          className="modern-btn"
          style={{
            background: costFilter === null ? "var(--snap-gold)" : "var(--snap-dark)",
            color: costFilter === null ? "#000" : "var(--snap-gold)",
            borderRadius: "8px",
            padding: "6px 12px",
            fontSize: "0.9rem",
          }}
          onClick={() => setCostFilter(null)}
        >
          All Costs
        </button>
        {costOptions.map((opt) => (
          <button
            key={opt}
            className="modern-btn"
            style={{
              background: costFilter === opt ? "var(--snap-gold)" : "var(--snap-dark)",
              color: costFilter === opt ? "#000" : "var(--snap-gold)",
              borderRadius: "8px",
              padding: "6px 12px",
              fontSize: "0.9rem",
            }}
            onClick={() => setCostFilter(opt)}
          >
            {opt === "0-1" ? "0–1" : opt}
          </button>
        ))}
      </div>

      {/* 🧩 Ability filters */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "center",
          gap: "8px",
          flexWrap: "wrap",
        }}
      >
        <button
          className="modern-btn"
          style={{
            background: abilityFilter === null ? "var(--snap-gold)" : "var(--snap-dark)",
            color: abilityFilter === null ? "#000" : "var(--snap-gold)",
            borderRadius: "8px",
            padding: "6px 12px",
            fontSize: "0.9rem",
          }}
          onClick={() => setAbilityFilter(null)}
        >
          All Abilities
        </button>
        {["On Reveal", "Ongoing", "Activate", "End of Turn"].map((opt) => (
          <button
            key={opt}
            className="modern-btn"
            style={{
              background: abilityFilter === opt ? "var(--snap-gold)" : "var(--snap-dark)",
              color: abilityFilter === opt ? "#000" : "var(--snap-gold)",
              borderRadius: "8px",
              padding: "6px 12px",
              fontSize: "0.9rem",
            }}
            onClick={() => setAbilityFilter(opt)}
          >
            {opt}
          </button>
        ))}
      </div>

      {/* 🧭 Archetype dropdown */}
      <div style={{ marginBottom: "20px" }}>
        <label style={{ color: "var(--snap-gold)", marginRight: "8px" }}>
          Filter by Archetype:
        </label>
        <select
          value={archetypeFilter}
          onChange={(e) => setArchetypeFilter(e.target.value)}
          style={{
            padding: "6px 12px",
            borderRadius: "8px",
            background: "#111",
            color: "#fff",
            border: "1px solid var(--snap-gold)",
          }}
        >
          <option value="All">All Archetypes</option>
          {archetypes.map((a) => (
            <option key={a.id} value={a.cardarchetype}>
              {a.cardarchetype}
            </option>
          ))}
        </select>
      </div>

      {/* 🃏 Card grid */}
      <div
        className="available-card-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: "12px",
          justifyItems: "center",
          alignItems: "center",
          marginTop: "10px",
        }}
      >
        {filteredCards.map((c) => (
          <div
            key={c.id}
            className="card-snap"
            onClick={() => handleCardClick(c.id)}
            style={{
              cursor: "pointer",
              background: "rgba(255,255,255,0.05)",
              padding: "6px",
              borderRadius: "8px",
              transition: "transform 0.15s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            <img
              src={c.cardhtml || c.cardHTML}
              alt={c.cardname}
              title={`${c.cardname} — Cost ${c.cardcost} / Power ${c.cardpower}`}
              style={{ borderRadius: "8px", width: "100%" }}
            />
            <p style={{ fontSize: "0.8rem", marginTop: "4px", color: "var(--snap-gold)" }}>
              {c.cardname}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default MakeDraftPick;
